<script setup lang="ts">
import { inject, computed } from 'vue'
import { useIncidentStore } from '@/stores/incident'
import { useOpsStore } from '@/stores/ops'
import { usePersonnelStore } from '@/stores/personnel'
import FormShell from '@/components/forms/FormShell.vue'
import { FormType } from '@/types'

const incidentStore = useIncidentStore()
const opsStore = useOpsStore()
const personnelStore = usePersonnelStore()
const incidentId = inject<string>('incidentId', '')

const incident = computed(() => incidentStore.currentIncident)
const op = computed(() => opsStore.selectedOp)

const groupedPersonnel = computed(() => {
  const roster = personnelStore.roster
  const groups: Record<string, typeof roster> = {}
  for (const p of roster) {
    const cat = p.category ?? 'General'
    if (!groups[cat]) groups[cat] = []
    groups[cat].push(p)
  }
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
})

function formatTime(ts: unknown): string {
  if (!ts) return '—'
  const d = (ts as { toDate?: () => Date }).toDate?.()
  if (!d) return '—'
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
</script>

<template>
  <FormShell
    :form-type="FormType.ICS211Header"
    title="ICS 211 · Agency Check-In List"
    back-route="incident-forms"
    back-label="Forms"
    :back-params="{ id: incidentId }"
  >
    <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
      <!-- Section head -->
      <div class="flex items-end gap-3.5 flex-wrap">
        <div class="flex flex-col gap-0.5">
          <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">ICS 211 · Agency Check-In List</span>
          <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Agency Check-In List</h2>
        </div>
        <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Live personnel roster for the current operational period. Updates in real time as personnel check in and out.</p>
      </div>

      <!-- Incident / OP info (read-only) -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Incident / Operational Period</h3>
          <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">Read-only</span>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Incident Name</label>
            <p class="font-body text-[13px] text-on-surface">{{ incident?.incident_name ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Incident Number</label>
            <p class="font-body text-[13px] text-on-surface">{{ incident?.incident_number ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Operational Period</label>
            <p class="font-mono text-[13px] text-on-surface">{{ op ? `OP ${op.op_number}` : '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">OP Date</label>
            <p class="font-body text-[13px] text-on-surface">{{ op?.date ?? '—' }}</p>
          </div>
        </div>
      </div>

      <!-- Personnel Roster -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Personnel Roster</h3>
          <span class="font-mono text-[11px] tracking-[0.06em] text-outline ml-auto">{{ personnelStore.roster.length }} personnel</span>
        </div>

        <div v-if="personnelStore.roster.length === 0" class="p-8 text-center">
          <p class="font-mono text-[12px] text-outline">No personnel checked in for this operational period.</p>
        </div>

        <div v-else class="flex flex-col divide-y divide-border/60">
          <div v-for="([category, members]) in groupedPersonnel" :key="category">
            <!-- Category header -->
            <div class="px-3.5 py-2 bg-surface-container-low">
              <span class="font-mono text-[10px] tracking-[0.12em] uppercase text-outline">{{ category }}</span>
              <span class="font-mono text-[10px] text-outline ml-2">{{ members.length }}</span>
            </div>
            <!-- Members table -->
            <table class="w-full text-[12px]">
              <thead>
                <tr class="border-b border-border/40">
                  <th class="text-left px-3.5 py-2 font-mono text-[10px] tracking-[0.08em] uppercase text-outline font-medium">Name</th>
                  <th class="text-left px-3.5 py-2 font-mono text-[10px] tracking-[0.08em] uppercase text-outline font-medium">Agency</th>
                  <th class="text-left px-3.5 py-2 font-mono text-[10px] tracking-[0.08em] uppercase text-outline font-medium hidden sm:table-cell">Resource</th>
                  <th class="text-left px-3.5 py-2 font-mono text-[10px] tracking-[0.08em] uppercase text-outline font-medium">Time In</th>
                  <th class="text-left px-3.5 py-2 font-mono text-[10px] tracking-[0.08em] uppercase text-outline font-medium">Time Out</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="p in members"
                  :key="p.personnel_id"
                  class="border-b border-border/30 last:border-0"
                >
                  <td class="px-3.5 py-2 font-body text-on-surface">{{ p.last }}, {{ p.first }}</td>
                  <td class="px-3.5 py-2 font-body text-outline">{{ p.agency ?? '—' }}</td>
                  <td class="px-3.5 py-2 font-body text-outline hidden sm:table-cell">{{ p.resource ?? '—' }}</td>
                  <td class="px-3.5 py-2 font-mono text-[11px] text-on-surface">{{ formatTime(p.time_in) }}</td>
                  <td class="px-3.5 py-2 font-mono text-[11px] text-outline">{{ p.time_out ? formatTime(p.time_out) : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </FormShell>
</template>
